import React, { useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import { useMutation } from 'react-query'
import { useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  LinearProgress,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { AuditCard, GenericCard, LocationCard } from '../ModelCard'
import { Audit, Container, Pallet, Place } from 'src/api/models'
import { Scanner } from '../Scanner'

import { urlFor } from '../../routes'
import { Event, ScanContext, ScanContextCallback } from '../../scanRouter/ScanContext'
import {
  Action,
  AssignmentHandler,
  AuditHandler,
  CaseLabelPrintingHandler,
  FoundHandler,
  Handler,
  LabelPrintingHandler,
  Mode,
  MoveContainerHandler,
  MoveHandler,
  MoveHandlerCallback,
  PreviewHandler,
  ReceivingHandler,
  SetSSCCHandler,
} from '../../scanRouter/handlers'
import { Notification } from '../Notification'
import toast from 'react-hot-toast'
import { skuit } from 'src/services/skuit'

import { AssignmentDisplay } from './AssignmentDisplay'
import { AuditDisplay } from './AuditDisplay'
import { CaseLabelPrintingDisplay } from './CaseLabelPrintingDisplay'
import { FoundDisplay } from './FoundDisplay'
import { MeasurementCard } from './MeasurementCard'
import { MoveContainerDisplay } from './MoveContainerDisplay'
import { MoveItemDisplay } from './MoveItemDisplay'
import { ReceivingDisplay } from './ReceivingDisplay'
import { LabelPrintingDisplay } from './LabelPrintingDisplay'
import { SetSSCCDisplay } from './SetSSCCDisplay'

const useStyles = makeStyles(() => ({
  progress: {
    height: '4px',
  },
}))

interface PreviewDisplayProps {
  handler: PreviewHandler
}

const PreviewDisplay = (props: PreviewDisplayProps) => {
  const { handler } = props
  if (!handler.obj?.object) {
    return null
  }
  const event = new CustomEvent('skuitCommand', { detail: { command: 'found' } })
  if ([Place.type, Container.type, Pallet.type].includes(handler.obj.object.type)) {
    return (
      <Grid container justify="center">
        <Grid item>
          <LocationCard item={handler.obj} onFound={() => window.dispatchEvent(event)} />
        </Grid>
      </Grid>
    )
  }
  if (handler.obj.object.type === Audit.type) {
    return (
      <Grid container justify="center">
        <Grid item>
          <AuditCard item={handler.obj.object} />
        </Grid>
      </Grid>
    )
  }
  return (
    <Grid container justify="center">
      <Grid item>
        <GenericCard item={handler.obj} />
      </Grid>
    </Grid>
  )
}

interface MoveDisplayProps {
  handler: MoveHandler
}

const MoveDisplay = (props: MoveDisplayProps) => {
  const { handler } = props
  const [mode, setMode] = useState<Mode>(Mode.Scan)
  const [error] = useState(false)
  const [, refresh] = useState({})

  useEffect(() => {
    const cb: MoveHandlerCallback = () => {
      refresh({})
    }
    handler.registerCb(cb)
    return () => handler.removeCb(cb)
  }, [handler, refresh])
  return (
    <Grid container justify="center" spacing={2}>
      <Grid item>
        <GenericCard item={handler.source} />
      </Grid>
      <Grid container item justify="center" spacing={6}>
        <Grid item>
          <Button
            color="primary"
            variant={mode === Mode.Scan ? 'contained' : 'outlined'}
            onClick={() => {
              handler.setMode(Mode.Scan)
              setMode(handler.mode)
            }}
          >
            Scan
          </Button>
        </Grid>
        <Grid item>
          <Button
            color="primary"
            variant={mode === Mode.Unscan ? 'contained' : 'outlined'}
            onClick={() => {
              handler.setMode(Mode.Unscan)
              setMode(handler.mode)
            }}
          >
            UnScan
          </Button>
        </Grid>
      </Grid>
      <>
        {handler.toMeasure ? (
          <MeasurementCard
            product={handler.toMeasure.product.object}
            unitOfMeasure={handler.toMeasure.sku?.object.unitOfMeasureDisplay}
            measure={handler.provideMeasure(handler.toMeasure)}
          />
        ) : (
            <>
              {handler.inventory.items.map((item) => (
                <MoveItemDisplay key={item.key} handler={handler} item={item} />
              ))}
            </>
          )}
        {!!error && (
          <>
            <Grid item>
              <Typography color="error">Failed move: {error}</Typography>
            </Grid>
            <Grid item>
              <Typography>Scan to restart scan sequence</Typography>
            </Grid>
          </>
        )}
      </>
    </Grid>
  )
}

interface HandlerRouterProps {
  handler?: Handler
}

const HandlerRouterRaw = (props: HandlerRouterProps) => {
  const { handler } = props
  if (handler instanceof PreviewHandler) {
    return <PreviewDisplay handler={handler} />
  } else if (handler instanceof MoveHandler) {
    return <MoveDisplay handler={handler} />
  } else if (handler instanceof MoveContainerHandler) {
    return <MoveContainerDisplay handler={handler} />
  } else if (handler instanceof ReceivingHandler) {
    return <ReceivingDisplay handler={handler} />
  } else if (handler instanceof FoundHandler) {
    return <FoundDisplay handler={handler} />
  } else if (handler instanceof AuditHandler) {
    return <AuditDisplay handler={handler} />
  } else if (handler instanceof AssignmentHandler) {
    return <AssignmentDisplay handler={handler} />
  } else if (handler instanceof LabelPrintingHandler) {
    return <LabelPrintingDisplay handler={handler} />
  } else if (handler instanceof CaseLabelPrintingHandler) {
    return <CaseLabelPrintingDisplay handler={handler} />
  } else if (handler instanceof SetSSCCHandler) {
    return <SetSSCCDisplay handler={handler} />
  }
  return null
}

const HandlerRouter = React.memo(HandlerRouterRaw)

export const ScanResultDisplay = () => {
  const history = useHistory()
  const classes = useStyles()
  const [scanContext] = useState(new ScanContext())
  const { mutate: move } = useMutation(skuit.sc.move.scan, {
    onSuccess: () => {
      toast.success(<Notification message="Move succeeded!" />)
      scanContext.reset()
    },
    onError: (error: AxiosError) => {
      toast.error(<Notification message={`Failed move. "${error.response?.data}"`} />)
    },
  })
  const [loading, setLoading] = useState(false)
  const [, refresh] = useState({})

  useEffect(() => {
    const cb: ScanContextCallback = ({ event, data }) => {
      switch (event) {
        case Event.Loading: {
          setLoading(data.loading)
          break
        }
        case Event.Reset: {
          refresh({})
          break
        }
        case Event.Action: {
          switch (data.action) {
            case Action.Navigate: {
              const url = urlFor(data.data.object)
              if (url) {
                scanContext.reset()
                if (url.startsWith('/v2/')) {
                  window.location.assign(url)
                } else {
                  history.push(url)
                }
              }
              break
            }
            case Action.Move: {
              move(data.data)
              break
            }
          }
        }
      }
    }
    scanContext.registerCb(cb)
    return () => scanContext.removeCb(cb)
  }, [scanContext, setLoading, history, move])

  const handler = scanContext.handler

  return (
    <>
      <Scanner onScan={scanContext.scan} onCommand={scanContext.command} />
      <Dialog
        open={!!scanContext.handler}
        onClose={() => {
          scanContext.reset()
        }}
        maxWidth="md"
      >
        <DialogContent>
          <Box className={classes.progress}>{loading && <LinearProgress />}</Box>
          <HandlerRouter handler={handler} />
        </DialogContent>
      </Dialog>
    </>
  )
}
